import { Component, OnInit, Input, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs/internal/Subject';
import { BehaviorSubject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { Link } from '../../interfaces/link.interface';
import { JobService } from '../../services/job.service';
import { LoggingUpdateSignalrService } from '../../services/signalR/logging-update-signalr.service';
import { LogTypePropertiesMapping } from '../../interfaces/logtype-properties-mapping.interface';
import { AsamcoAuthService, AsamcoUserService } from '@asamco/base';
import { bind } from '@asamco/utils';

import { DxTemplateModule } from 'devextreme-angular/core';
import {
	DxoLoadPanelModule,
	DxoPagerModule,
	DxoScrollingModule,
	DxiColumnModule,
	DxoFilterRowModule,
	DxoPagingModule,
	DxoSelectionModule,
	DxiButtonModule
} from 'devextreme-angular/ui/nested';

import {
	NgIf, NgFor, NgSwitch, NgSwitchCase, NgSwitchDefault,
	AsyncPipe, DatePipe, JsonPipe, NgClass
} from '@angular/common';

import { DxPopupModule, DxDataGridModule, DxScrollViewModule, DxTextBoxModule } from 'devextreme-angular';
import { ALTabsComponent, FreshdeskHelpModalComponent, FreshdeskSolutionService, FreshdeskSolutionViewerComponent, JobLogService, JobLogType, MissingArticleFeedbackComponent } from '@asamco/application-link/shared';
import ODataStore from 'devextreme/data/odata/store';

import { TimeDifferencePipe } from '../../pipes/timedifference.pipe';
import { AlCompanyService } from '../../services/al-company.service';
import { SelectedCompanyService } from '../../selected-company/selected-company.service';
import { TimezoneDatePipe } from '../../pipes/timezone_date.pipe';
import { JobLogHistory } from '../../interfaces/job-log-history.interface';

import { FormsModule } from '@angular/forms';

type IssueArticleVm = {
	articleId: number;
	title: string;
	bodyHtml: string;
	articleCreatedAt?: string | Date | null;
	articleEditedAt?: string | Date | null;
};

@Component({
	selector: 'al-link-log',
	templateUrl: './link-log.component.html',
	styleUrls: ['./link-log.component.scss'],
	standalone: true,
	imports: [
		DxPopupModule,
		NgIf,
		NgFor,
		NgSwitch,
		NgSwitchCase,
		NgSwitchDefault,
		NgClass,

		DxDataGridModule,
		DxoLoadPanelModule,
		DxoPagerModule,
		DxoScrollingModule,
		DxiColumnModule,
		DxoFilterRowModule,
		DxoPagingModule,
		DxoSelectionModule,
		DxiButtonModule,
		DxTemplateModule,
		ALTabsComponent,
		DxScrollViewModule,
		DxTextBoxModule,
		AsyncPipe,
		DatePipe,
		TimeDifferencePipe,
		TimezoneDatePipe,
		JsonPipe,
		FormsModule,

		// NEW (only needed because HTML uses <app-freshdesk-solution-viewer>)
		FreshdeskSolutionViewerComponent,
		FreshdeskHelpModalComponent,
		MissingArticleFeedbackComponent
	],
})
export class LinkLogComponent implements OnInit, OnDestroy {
	@Input()
	link: Link;

	@Input()
	cspMode: boolean = false;

	@ViewChild('missingArticleFeedback') missingArticleFeedback?: MissingArticleFeedbackComponent;

	@ViewChild('helpModal') helpModal?: FreshdeskHelpModalComponent;

	SelectedProperties: string[] = [];
	SelectedDataSource: any = [];
	SelectedLogType: JobLogType;

	public LogTypPropertiesMapping: BehaviorSubject<LogTypePropertiesMapping[]> = new BehaviorSubject<LogTypePropertiesMapping[]>(
		[] as LogTypePropertiesMapping[]
	);

	private destroy$ = new Subject<void>();

	isSignalrActive: boolean = false;
	enumList: JobLogType[];
	gridColumns: string[] = ['CreationDate', 'TotalSucceeded', 'TotalFailed'];
	public selectedJobInstanceId: string[] = [];

	tabs: any[] = [];
	public tabsSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
	public selectedTab$: BehaviorSubject<number> = new BehaviorSubject<number>(JobLogType.All);

	entriesAvailable: boolean = false;

	public isExpandedMessageVisible: boolean = false;
	public isExpandedMessageSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

	public hasLogBeenClicked: boolean = false;

	singleModeSelectionSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	public isAdminSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public adminInstanceLog: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

	public jobDataSourceOData: ODataStore<JobLogHistory> = null;
	companyInfo$: BehaviorSubject<{timezone: string, dateformat: string}> = new BehaviorSubject<{timezone: string, dateformat: string}>(null);

	// ===== NEW: Issue help popup state (viewer-based) =====
	public isIssueHelpVisible: boolean = false;
	public issueLoading: boolean = false;
	public issueArticles: IssueArticleVm[] = [];
	public issueSelectedArticle: IssueArticleVm | null = null;

	constructor(
		private loggingUpdateService: LoggingUpdateSignalrService,
		private jobLogService: JobLogService,
		private jobService: JobService,
		private asamcoUserService: AsamcoUserService,
		private asamcoAuthService: AsamcoAuthService,
		private companyService: AlCompanyService,
		private selectedCompanyService: SelectedCompanyService,

		// NEW
		private freshdeskSolutionService: FreshdeskSolutionService
	) {
		this.getCurrentTimeZoneIfExists();
		this.selectedTab$.subscribe(tabId => this.changeDataSource(tabId));

		this.asamcoAuthService.authTokenChanged$.subscribe(
			(token) => {
				if (token.token) {
					this.jobDataSourceOData = new ODataStore<JobLogHistory>({
						deserializeDates: false,
						url: this.asamcoAuthService.userSession.backend.odataBaseUrl + 'JobLogHistories',
						version: 4,
						beforeSend: (e) => {
							e.headers = {
								Authorization: `Bearer ${token.token}`,
								'X-company-id': this.link.companyId,
								'X-link-id': this.link.id,
							};
						}
					});
				}
			},
			(error) => {
				console.error('Error fetching the token:', error);
			}
		);
	}

	getCurrentTimeZoneIfExists() {
		this.selectedCompanyService.getCompanyIdObservable().subscribe(e => {
			this.companyService.getSingleCompany(e).subscribe(company => {
				this.companyInfo$.next({
					timezone: company.timezoneString ? company.timezoneString : 'UTC',
					dateformat: company.dateFormat ? company.dateFormat : 'dd-MM-yyyy' }
				);
			});
		});
	}

	requestHelpForRow(row: any) {
		if (!row) return;
		// optional guard, keep if you want:
		if (row?.jobLogType != 4) return;

		// call your existing logic or open the missingArticleFeedback directly
		this.requestHelpButton({ row: { data: row, key: row } });
		// or: this.missingArticleFeedback?.open(...)
	}

	hasAnyArticleIds(value: any[] | null | undefined): boolean {
  return Array.isArray(value) && value.some(x => Array.isArray(x?.articleIds) && x.articleIds.length > 0);
}

	@bind
	requestHelpButton(e: any) {
		const row = e?.row?.key ?? e?.row?.data ?? e?.data;
		if (!row) return;

		// Only for Error type (your enum shows '4' = Error)
		const t = String(row.jobLogType ?? '');
		if (t !== '4') return;

		const origin = {
			kind: 'link_logs' as const,
			linkId: this.link.id,
			instanceId: this.selectedJobInstanceId?.[0] ?? null,
			jobLogType: row.jobLogType ?? null,
			jobLogId: row.jobLogId ?? row.id ?? null,
			logMessage: row.message ?? row.description ?? null,
			pattern: { id: null },
		};

		this.missingArticleFeedback?.open(origin);
	}

	cellClicked($event) {
		if ($event.rowType === 'data') {
			this.getJobsByJobId($event.data.instanceId);
		}
	}

	public isArticleBrowserCollapsed = false;

	toggleArticleBrowser(): void {
		this.isArticleBrowserCollapsed = !this.isArticleBrowserCollapsed;
	}

	public issueSearchTerm = '';

	public onIssueSearch(evt: Event): void {
		this.issueSearchTerm = (evt.target as HTMLInputElement)?.value ?? '';
	}

	get filteredIssueArticles() {
		const list = this.issueArticles ?? [];
		const q = (this.issueSearchTerm || '').trim().toLowerCase();
		if (!q) return list;

		return list.filter(a =>
			(a?.title || '').toLowerCase().includes(q)
		);
	}

	setupTabs() {
		let index = 0;

		this.enumList.forEach((element: JobLogType) => {
			if (index !== JobLogType.All) {
				const itemCount = this.LogTypPropertiesMapping.getValue().filter(item => Number(item.LogType) === index);
				if (itemCount.length > 0) {
					let itemName: string = element.toString();
					if (index === JobLogType.AmountMismatch) {
						itemName = 'Mismatch';
					}

					this.tabs.push({ id: index, text: itemName + ' (' + itemCount[0].dataSource.length + ')', visible: false });
					this.tabsSubject.next(this.tabs);
				}
			}
			index = index + 1;
		});

		this.tabs.unshift({ id: JobLogType.All, text: this.enumList[JobLogType.All], visible: true });
		this.tabsSubject.next(this.tabs);
	}

	hasAnyHelpArticles(ds: any[]): boolean {
		return (ds ?? []).some(r => Array.isArray(r?.helpArticles) && r.helpArticles.length > 0);
	}

	getColumns(item: any[]) {
  this.LogTypPropertiesMapping.next([] as LogTypePropertiesMapping[]);

  item.forEach((e: any[]) => {
    e.forEach(b => {
      if (b.jobLogType && !this.LogTypPropertiesMapping.getValue().find(x => x.LogType == b.jobLogType)) {
        let tempProperty: string[] = Object.getOwnPropertyNames(b);

        tempProperty = tempProperty.filter(x => x !== 'creationDate');
        tempProperty = tempProperty.filter(x => x !== 'jobLogType');
        tempProperty = tempProperty.filter(x => x !== 'rowColor');
        tempProperty = tempProperty.filter(x => x !== 'jobLogId');

        // ✅ Always include helpArticles (even if backend didn't send it on some rows)
        if (!tempProperty.includes('helpArticles')) {
          tempProperty.push('helpArticles');
        } else {
          // keep it last
          tempProperty = tempProperty.filter(x => x !== 'helpArticles');
          tempProperty.push('helpArticles');
        }

        this.LogTypPropertiesMapping.getValue().push({
          LogType: b.jobLogType,
          properties: tempProperty,
          dataSource: e,
        });
      }
    });
  });
}


	isWideColumn(columnName: string) {
		return ['message', 'description', 'difference'].includes(columnName);
	}

	StringIsNumber = value => isNaN(Number(value)) === false;

	toArray(enumme) {
		return Object.keys(enumme)
			.filter(this.StringIsNumber)
			.map(key => enumme[key]);
	}

	changeDataSource(joblogType) {
		if (joblogType === JobLogType.All) {
			this.singleModeSelectionSubject.next(false);
		} else {
			const item = this.LogTypPropertiesMapping.getValue().filter(item => item.LogType == joblogType.toString())[0];
			this.SelectedDataSource = item.dataSource;
			this.SelectedProperties = item.properties;
			this.SelectedLogType = item.LogType;
			this.singleModeSelectionSubject.next(true);
		}
	}

	enableSignalR() {
		this.loggingUpdateService
			.getLoggerObservable()
			.pipe(takeUntil(this.destroy$))
			.subscribe(loggingUpdate => {
				this.isSignalrActive = true;

				if (loggingUpdate.jobInstanceId !== this.selectedJobInstanceId[0]) return;

				const logTypeProperties = this.LogTypPropertiesMapping.getValue();
				const matchingLogType = logTypeProperties.find(item => item.LogType === loggingUpdate.jobLogType);

				if (matchingLogType) {
					const currentDataSource = matchingLogType.dataSource;
					const mostRecentJobLogId = Math.max(...currentDataSource.map(o => Number(o.jobLogId)));

					this.jobLogService
						.getLatestFieldsByLogType({
							instanceId: this.selectedJobInstanceId[0],
							latestJobLogId: mostRecentJobLogId,
							jobLogType: loggingUpdate.jobLogType,
						})
						.subscribe(newLogs => {
							const item = logTypeProperties.find(prop => prop.LogType === newLogs[0].jobLogType);
							if (item) newLogs.forEach(element => item.dataSource.push(element));
						});
				} else {
					this.getJobsByJobId(this.selectedJobInstanceId[0]);
				}
			});
	}

	getJobsByJobId(instanceId: string) {
		this.jobLogService
			.get(instanceId)
			.pipe(
				tap(item => this.getColumns(item)),
				takeUntil(this.destroy$)
			)
			.subscribe(_ => {
				this.hasLogBeenClicked = true;
				if (this.LogTypPropertiesMapping.getValue().length > 0) {
					this.entriesAvailable = true;
					this.getDistinctItems();
					this.singleModeSelectionSubject.next(false);
				} else {
					this.entriesAvailable = false;
				}
			});
	}

	onRowPrepared(e) {
		if (e.rowType === 'data' && e.data.rowColor) {
			e.rowElement.style.backgroundColor = e.data.rowColor;
			e.rowElement.className = e.rowElement.className.replace('dx-row-alt', '');
		}
	}

	getDistinctItems() {
		this.tabs = [];
		this.tabsSubject.next(this.tabs);
		this.setupTabs();

		const unique = [...new Set(this.LogTypPropertiesMapping.getValue().map(item => item.LogType))];
		this.tabs.forEach(e => {
			if (unique.includes(e.id.toString())) {
				e.visible = true;
			}
		});
		this.tabsSubject.next(this.tabs);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public get JobLogType(): typeof JobLogType {
		return JobLogType;
	}

	hideExpandedMessageVisible(e) {
		console.log(e);
	}

	@bind
	expandMessageButton(e) {
		if (e.row.key.message) {
			this.isExpandedMessageSubject.next(e.row.key.message);
			this.isExpandedMessageVisible = true;
		}
		else if (e.row.key.description) {
			this.isExpandedMessageSubject.next(e.row.key.description);
			this.isExpandedMessageVisible = true;
		}
		else if (e.row.key.difference) {
			this.isExpandedMessageSubject.next(e.row.key.difference);
			this.isExpandedMessageVisible = true;
		}
	}

	// ============================
	// NEW: Issue help popup logic (calls FreshdeskSolutionController endpoint)
	// ============================

	openIssueHelp(mp: any, rowMessage?: string | null): void {
		const ids: number[] = mp?.articleIds ?? [];
		if (!ids.length) return;

		const origin = {
			kind: 'link_logs' as const,
			linkId: this.link.id,
			pattern: {
				id: mp?.id ?? null,
			},

			// Include "Message" in the origin for potential use in the help modal (e.g. to show context or pass to Freshdesk for relevance scoring)
			logMessage: rowMessage ?? null,
		};

		this.helpModal?.open('Issue help', ids, origin);
	}

	ngOnInit(): void {
		this.enumList = this.toArray(JobLogType);

		this.asamcoUserService.isAdmin().then(result => {
			this.isAdminSubject.next(result);
		});
	}
}
